// Users (admins): invite by email with a role (admin / agent / viewer) and, optionally, only some WhatsApp numbers;
// change or remove roles; see who has two-step sign-in; reset a lost second factor; require two-step sign-in for all.
import { callFn, rpc, sb } from "../sb.ts";
import { t } from "../i18n.ts";
import { fmtTime, h, mount, toast } from "../ui.ts";

interface U { user_id: string; email: string; role: string | null; lines: string[] | null; name: string | null; invited_at: string;
  last_sign_in_at: string | null; mfa: boolean }

export async function users(view: HTMLElement) {
  const box = h("section", { class: "page" });
  mount(view, box);
  const { data: { user } } = await sb.auth.getUser();
  const { data: lines } = await sb.from("wa_lines").select("line, label").order("created_at");
  const roleName = (r: string | null) => r ? t(r) : t("no access");

  const linePicker = (sel: string[] | null) => {
    const all = h("input", { type: "checkbox", checked: !sel });
    const boxes = (lines ?? []).map((l) => ({ l, el: h("input", { type: "checkbox", checked: !!sel?.includes(l.line), disabled: !sel }) }));
    all.addEventListener("change", () => boxes.forEach((b) => { b.el.disabled = all.checked; }));
    const el = h("div", { class: "lines" }, h("label", { class: "inline" }, all, " " + t("All numbers")),
      boxes.map((b) => h("label", { class: "inline", dir: "ltr" }, b.el, ` ${b.l.label || "+" + b.l.line}`)));
    // null = every number; otherwise at least one must be ticked (an empty list would mean no access at all)
    return { el, value: () => (all.checked ? null : boxes.filter((b) => b.el.checked).map((b) => b.l.line)),
      ok: () => all.checked || boxes.some((b) => b.el.checked) };
  };

  async function render() {
    let list: U[] = [];
    try { list = await rpc<U[]>("list_users"); } catch (e) { mount(box, h("p", { class: "err" }, (e as Error).message)); return; }
    const { data: st } = await sb.from("settings").select("require_mfa").maybeSingle();
    const email = h("input", { type: "email", placeholder: "name@company.com", dir: "ltr" });
    const role = h("select", {}, ["agent", "viewer", "admin"].map((r) => h("option", { value: r }, t(r))));
    const pick = linePicker(null);
    const invite = h("button", { class: "primary", onclick: async () => {
      if (!pick.ok()) return toast(t("Tick at least one number, or All numbers"), true);
      invite.disabled = true;
      const r = await callFn("staff", { mode: "invite", email: email.value, role: role.value, lines: pick.value(), redirect: location.origin + import.meta.env.BASE_URL });
      invite.disabled = false;
      if (!r.ok) return toast(r.error ?? "", true);
      toast(r.invited ? t("Invitation sent to {email}", { email: email.value }) : t("Role set (the user already had an account)"));
      render();
    } }, t("Invite"));
    const mfaAll = h("input", { type: "checkbox", checked: !!st?.require_mfa, onchange: async () => {
      try { await rpc("set_require_mfa", { p_on: mfaAll.checked }); toast(t("Saved")); }
      catch (e) { mfaAll.checked = !mfaAll.checked; toast((e as Error).message, true); }
    } });
    mount(box,
      h("h2", {}, t("Users")),
      h("div", { class: "card-in" }, h("h3", {}, t("Invite someone")),
        h("p", { class: "muted" }, t("They get an email with a link to choose a password. Admin: everything. Agent: chats and contacts. Viewer: reads only.")),
        h("div", { class: "row" }, h("label", {}, t("Email"), email), h("label", {}, t("Role"), role)), pick.el,
        h("div", { class: "row end" }, invite)),
      h("div", { class: "card-in" }, h("label", { class: "inline" }, mfaAll, " " + t("Everyone must use two-step sign-in")),
        h("p", { class: "muted" }, t("Users without it can still sign in and set it up under My account, but see nothing until they do."))),
      h("table", { class: "users" }, h("thead", {}, h("tr", {}, [t("User"), t("Role"), t("Numbers"), t("Two-step"), t("Last sign-in"), ""].map((x) => h("th", {}, x)))),
        h("tbody", {}, list.map((u) => row(u)))));
  }

  function row(u: U) {
    const self = u.user_id === user?.id;
    const role = h("select", { disabled: self }, h("option", { value: "" }, t("no access")),
      ["admin", "agent", "viewer"].map((r) => h("option", { value: r, selected: u.role === r }, t(r))));
    const pick = linePicker(u.lines);
    const save = async () => {
      if (role.value && !pick.ok()) return toast(t("Tick at least one number, or All numbers"), true);
      if (!role.value && !confirm(t("Remove {email}'s access?", { email: u.email }))) return;
      try { await rpc("set_user_role", { p_email: u.email, p_role: role.value, p_lines: role.value ? pick.value() : null }); toast(t("Saved")); render(); }
      catch (e) { toast((e as Error).message, true); }
    };
    return h("tr", {},
      h("td", {}, h("b", { dir: "auto" }, u.name && u.name !== u.email ? u.name : ""), h("div", { dir: "ltr" }, u.email), self && h("small", { class: "muted" }, t("(you)"))),
      h("td", {}, self ? roleName(u.role) : role),
      h("td", {}, self ? (u.lines ? u.lines.map((l) => "+" + l).join(", ") : t("All numbers")) : pick.el),
      h("td", {}, u.mfa ? "✓" : "—", u.mfa && !self && h("button", { class: "link", title: t("Reset (lost phone)"), onclick: async () => {
        if (!confirm(t("Remove {email}'s second factor? They set it up again at their next sign-in.", { email: u.email }))) return;
        const r = await callFn("staff", { mode: "reset_mfa", email: u.email });
        if (!r.ok) return toast(r.error ?? "", true);
        toast(t("Reset")); render();
      } }, "↺")),
      h("td", {}, u.last_sign_in_at ? fmtTime(u.last_sign_in_at) : t("invited")),
      h("td", {}, !self && h("button", { onclick: save }, t("Save"))));
  }
  await render();
}
